import React from 'react';

import { Box, Button, Grid, Typography } from '@mui/material';

import { Nullable } from '@clearverify/types';

import {
  useGenerateMyDataPdfMutation,
  useGetEmployeeDataForMyDataQuery,
  useGetEmploymentDataForEmployeeMyDataLazyQuery,
  useGetEmploymentDataForEmployeeMyDataQuery,
} from 'src/api';
import { PositionDetailsContent } from 'src/components/position';
import { ModalId } from 'src/constants/modals';
import { useScreenInfo } from 'src/hooks/common';
import { useCurrentUserEmployee } from 'src/hooks/employee';
import { useModal } from 'src/hooks/shared';
import { PositionDetailsModalArgs } from 'src/modals/position';
import { downloadFileByDownloadUrl } from 'src/utils/file';

import { EmployeeDataEmploymentRecordCard } from './EmployeeDataEmploymentRecordCard';

export const EmployeeMyData: React.FC = () => {
  const { loading: isEmployeeLoading } = useCurrentUserEmployee();
  const { isMobile } = useScreenInfo();
  const { openModal } = useModal();

  const [selectedCompanyId, setSelectedCompanyId] = React.useState<Nullable<string>>(null);

  const { consumer } = useCurrentUserEmployee();

  const { data: employeeData, loading: employeeDataLoading } = useGetEmployeeDataForMyDataQuery();

  const { data: employmentData, loading: employmentDataLoading } =
    useGetEmploymentDataForEmployeeMyDataQuery({
      variables: {
        companyId: selectedCompanyId || '',
      },
      skip: !selectedCompanyId,
    });
  const [getEmploymentData, { loading: employmentDataLazyLoading }] =
    useGetEmploymentDataForEmployeeMyDataLazyQuery();

  const [generateMyDataPdfMutation, { loading: generating }] = useGenerateMyDataPdfMutation();

  const handleDownloadDataClick = React.useCallback(async () => {
    const { data: result } = await generateMyDataPdfMutation();

    if (!result?.generateMyDataPdf) {
      return;
    }

    downloadFileByDownloadUrl(
      result.generateMyDataPdf.downloadUrl,
      result.generateMyDataPdf.filename,
    );
  }, [generateMyDataPdfMutation]);

  const handleDataDispute = React.useCallback(() => {
    if (employmentData?.getEmploymentDataForEmployeeMyData?.meta.employerId) {
      openModal(ModalId.CreateDataDisputeModal, {
        employerId: employmentData?.getEmploymentDataForEmployeeMyData?.meta.employerId,
      });
    }
  }, [openModal, employmentData?.getEmploymentDataForEmployeeMyData?.meta.employerId]);

  const handleChangeView = React.useCallback(
    async (companyId: string) => {
      if (isMobile) {
        const { data: employmentData } = await getEmploymentData({
          variables: { companyId },
        });
        openModal(ModalId.PositionDetails, {
          employmentData: employmentData?.getEmploymentDataForEmployeeMyData,
          onDataDispute: handleDataDispute,
        } satisfies PositionDetailsModalArgs);
        return;
      }
      setSelectedCompanyId(companyId);
    },
    [isMobile, getEmploymentData, openModal, handleDataDispute],
  );

  const isEmploymentDataEmpty =
    employeeData?.getEmployeeDataForMyData.employmentRecords.length === 0;
  const dataIsDeleted = !!consumer?.dataDeletedAt;

  if (selectedCompanyId) {
    return (
      <PositionDetailsContent
        employmentData={employmentData?.getEmploymentDataForEmployeeMyData}
        loading={employmentDataLoading}
        onBack={() => setSelectedCompanyId(null)}
        onDataDispute={handleDataDispute}
        isWatermarked
      />
    );
  }

  return (
    <React.Fragment>
      <Grid container alignItems="center" spacing={2}>
        {!isMobile && (
          <Grid size={{ xs: 6 }}>
            <Typography variant="body1" fontWeight="bold">
              Employment Information
            </Typography>
          </Grid>
        )}

        <Grid size={{ xs: isMobile ? 12 : 6 }}>
          <Box
            display="flex"
            gap={theme => theme.spacing(2)}
            justifyContent={isMobile ? 'center' : 'flex-end'}
          >
            <Button
              onClick={handleDownloadDataClick}
              variant="outlined"
              loading={generating}
              disabled={
                generating || isEmployeeLoading || employeeDataLoading || isEmploymentDataEmpty
              }
            >
              Download Data
            </Button>
          </Box>
        </Grid>
      </Grid>

      <Box display="flex" flexDirection="column" gap={2} mt={2}>
        {dataIsDeleted && (
          <Typography variant="body1">
            There is currently no information about your employments on the platform because of a
            data delete request that you initiated earlier. While the request is active, your
            employment data will not appear here. Please contact support if you would like to cancel
            the request.
          </Typography>
        )}

        {!dataIsDeleted && isEmploymentDataEmpty && (
          <Typography variant="body1">
            No employment information is available yet. It will appear here once your employer
            uploads your data to the platform.
          </Typography>
        )}

        {employeeData?.getEmployeeDataForMyData.employmentRecords.map(employmentRecord => (
          <EmployeeDataEmploymentRecordCard
            key={employmentRecord.companyId}
            employmentRecord={employmentRecord}
            onSeeMore={handleChangeView}
            loading={employmentDataLoading || employmentDataLazyLoading}
            hideEmployerLiveIndicator
          />
        ))}
      </Box>
    </React.Fragment>
  );
};
